import { field, getPublicClient, readContract, zeroAddress } from "../viem";
import config from "../../config/config";
import { City } from "../../constants/const";
import MafiaMapAbi from "../../abis/MafiaMap.json";

const address = config.mapAddress;
const abi = MafiaMapAbi;

export interface CityDeveloperInfo {
  netWorth: string;
  developer: string;
}

const normalizeAddress = (value: string) => value.trim().toLowerCase();

const sameAddress = (left: string, right: string) =>
  normalizeAddress(left) === normalizeAddress(right);

const isAssignedDeveloper = (developer: string) => {
  const normalized = normalizeAddress(developer);
  return normalized !== "" && normalized !== zeroAddress;
};

const parseCityDeveloper = (data: unknown): CityDeveloperInfo => ({
  netWorth: String(field(data, "netWorth", 0) ?? "0"),
  developer: String(field(data, "developer", 1) ?? zeroAddress),
});

export const getUserSlotCount = async (account: string) => {
  const slotCount = await readContract<string>({
    address,
    abi,
    functionName: "userSlotCount",
    args: [account],
    account,
  });

  return Number(slotCount);
};

/** `cityDevelopers(cityId)` — net worth and the address that develops that city. */
export const getCityDeveloperInfo = async (
  cityId: number
): Promise<CityDeveloperInfo> => {
  const data = await readContract({
    address,
    abi,
    functionName: "cityDevelopers",
    args: [cityId],
  });

  return parseCityDeveloper(data);
};

export const getCityDevelopers = async (): Promise<CityDeveloperInfo[]> => {
  const results = await getPublicClient().multicall({
    contracts: City.map((_, cityId) => ({
      address: address as `0x${string}`,
      abi,
      functionName: "cityDevelopers",
      args: [cityId],
    })) as any,
    allowFailure: false,
  });

  return results.map((result) => parseCityDeveloper(result));
};

export const getIsCityDeveloper = async (
  account: string,
  cityId: number
): Promise<boolean> => {
  if (!account) return false;

  const { developer } = await getCityDeveloperInfo(cityId);
  return isAssignedDeveloper(developer) && sameAddress(developer, account);
};

/** Every city the account currently develops. */
export const getCityDeveloperCityIds = async (
  account: string
): Promise<number[]> => {
  if (!account) return [];

  const developers = await getCityDevelopers();
  return developers.flatMap((info, cityId) =>
    isAssignedDeveloper(info.developer) && sameAddress(info.developer, account)
      ? [cityId]
      : []
  );
};
